import React, { useCallback, useEffect, useState } from "react";
import DadosEstadoCivilDefaultForm from "./DadosEstadoCivilDefaultForm";
import { useDispatch, useSelector } from "react-redux";
import useCompleteForm from "../../../../hooks/useCompleteForm";
import validators from "../../../../utils/validators";
import { actions } from "../../../../actions/clientes.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../utils/basic";
import formatters from "../../../../utils/formatters";
import {
  getPreenchimentoCliente,
  isEnviandoPreenchimentoCliente,
  isGravandoPreenchimentoCliente,
} from "../../../../selectors/clientes.selectors";

const DadosEstadoCivilDefaultFormConnected = ({
  handleGoTo3,
  events,
  ...props
}) => {
  const dispatch = useDispatch();
  const {
    cliente: clienteData,
    dominios: {
      estadoCivil: estadosCivis,
      nacionalidade: nacionalidades,
      regimeComunhao: regimesComunhao,
      genero: generos,
      documentoPessoal: documentosPessoais,
    },
    validacao,
  } = useSelector(getPreenchimentoCliente);
  const sending = useSelector(isEnviandoPreenchimentoCliente);
  const submitting = useSelector(isGravandoPreenchimentoCliente);

  const cliente = {
    ...clienteData,
    fluxo: validacao.completa.cliente,
  };

  const estadoCivilInicial =
    estadosCivis.find((e) => e.id === cliente?.estadoCivil?.id) ?? null;
  const estadoCivilConjugeInicial =
    estadosCivis.find((e) => e.id === cliente?.conjuge?.estadoCivil?.id) ??
    null;

  const callback = useCallback(() => {
    handleGoTo3();
  }, [handleGoTo3]);

  const requiredIfContainsSpouse = (value, values) => {
    const exibeConjuge = values.estadoCivil?.conjuge || values.uniaoEstavel;

    if (!exibeConjuge) {
      return true;
    }

    if (value !== undefined && value !== null && value !== "" && value !== {})
      return true;
    return "Campo obrigatório";
  };
  const requiredIfContainsSpouseNotMarried = (value, values) => {
    const exibeConjuge = values.estadoCivil?.conjuge || values.uniaoEstavel;
    const exibeExtra = exibeConjuge && values.isUniaoEstavel;

    if (!exibeExtra) {
      return true;
    }

    if (value !== undefined && value !== null && value !== "" && value !== {})
      return true;
    return "Campo obrigatório";
  };

  const [formProps, handleSubmit] = useCompleteForm({
    rules: () => ({
      estadoCivil: validators.object({ required: true }),
      dataCasamento: validators.date({
        custom: {
          requiredIfContainsSpouse,
        },
      }),
      regimeComunhao: validators.object({
        custom: {
          requiredIfContainsSpouse,
        },
      }),
      conjuge: {
        nome: validators.string({
          custom: {
            requiredIfContainsSpouse,
            nomeCompleto: (value) =>
              !value ||
              (value.indexOf(" ") > 0 &&
                value.substring(value.indexOf(" ") + 1).length > 0) ||
              "Nome informado deve ser completo",
          },
        }),
        cpfCnpj: validators.cpf({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        dataNascimento: validators.date({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        genero: validators.object({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        documentoPessoal: validators.object({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        numeroDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
          maxLength: 100,
        }),
        orgaoDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
          maxLength: 100,
        }),
        ufOrgaoDocumentoPessoal: validators.string({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        dataExpedicaoDocumentoPessoal: validators.date({
          custom: {
            requiredIfContainsSpouse,
          },
          min: "01/01/1900",
          max: formatters.dates.short(new Date()),
        }),
        email: validators.email({
          custom: {
            requiredIfContainsSpouse,
          },
        }),
        estadoCivil: validators.object({
          custom: { requiredIfContainsSpouseNotMarried },
        }),
      },
    }),
    initialValues: useCallback(
      () => ({
        uniaoEstavel: false,
        regimeComunhao: null,
        dataCasamento: "",
        ...cliente,
        estadoCivil: estadoCivilInicial,
        conjuge: {
          nome: "",
          cpfCnpj: "",
          dataNascimento: "",
          telefone: "",
          nacionalidade: null,
          compoeRenda: false,
          ...cliente?.conjuge,
          estadoCivil: estadoCivilConjugeInicial,
        },
      }),
      [cliente, estadoCivilInicial, estadoCivilConjugeInicial]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            passo: "dados-estado-civil",
            cliente: {
              id: cliente?.id,
              idEstadoCivil: values.estadoCivil?.id,
              uniaoEstavel: exibeUniaoEstavel && values.uniaoEstavel,
              dataCasamento: !!exibeConjuge
                ? transformToDate(values.dataCasamento)
                : null,
              idRegimeComunhao: !!exibeConjuge
                ? values.regimeComunhao?.id
                : null,
              conjuge: !!exibeConjuge
                ? {
                    nome: values.conjuge.nome,
                    cpfCnpj: removeNonDigitsFromString(values.conjuge.cpfCnpj),
                    dataNascimento: transformToDate(
                      values.conjuge.dataNascimento
                    ),
                    idGenero: values.conjuge.genero?.id,
                    email: values.conjuge.email,
                    idEstadoCivil:
                      !!exibeUniaoEstavel && values.uniaoEstavel
                        ? values.conjuge?.estadoCivil?.id
                        : null,
                    idDocumentoPessoal: values.conjuge.documentoPessoal?.id,
                    numeroDocumentoPessoal:
                      values.conjuge.numeroDocumentoPessoal,
                    orgaoDocumentoPessoal: values.conjuge.orgaoDocumentoPessoal,
                    ufOrgaoDocumentoPessoal:
                      values.conjuge.documentoPessoal?.descricao === "RNE"
                        ? null
                        : values.conjuge.ufOrgaoDocumentoPessoal,
                    dataExpedicaoDocumentoPessoal: transformToDate(
                      values.conjuge.dataExpedicaoDocumentoPessoal
                    ),
                  }
                : null,
            },
          })
        );
      },
      [dispatch, cliente, callback]
    ),
  });

  const { setValue, watch } = formProps;

  const estadoCivil = watch("estadoCivil");
  const isUniaoEstavel = watch("uniaoEstavel");
  const isEstrangeiro = watch("conjuge.documentoPessoal")?.id === 4;

  const exibeUniaoEstavel = !!estadoCivil?.uniaoEstavel;
  const exibeConjuge = !!estadoCivil?.conjuge || isUniaoEstavel;

  useEffect(() => {
    if (!!estadoCivil?.conjuge) {
      setValue("uniaoEstavel", false);
    }
  }, [estadoCivil, setValue, isUniaoEstavel]);
  
  return (
    <DadosEstadoCivilDefaultForm
      {...props}
      estadosCivis={estadosCivis}
      nacionalidades={nacionalidades}
      events={events}
      fluxo={cliente.fluxo}
      formProps={formProps}
      handleSubmit={handleSubmit}
      exibeConjuge={exibeConjuge}
      exibeUniaoEstavel={exibeUniaoEstavel}
      submitting={submitting}
      regimesComunhao={regimesComunhao}
      generos={generos}
      documentosPessoais={documentosPessoais}
      isEstrangeiro={isEstrangeiro}
    />
  );
};

export default DadosEstadoCivilDefaultFormConnected;
